import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';

import { useCustomStore } from '../../../../hooks';
import type { MotorClubTrafficIncidentDetails } from '../../../../store/api/motorClubMembership.api';

interface ViewTrafficIncidentModalProps {
  incidentId: string;
  onClose: () => void;
  open: boolean;
}

const ViewTrafficIncidentModal: React.FC<ViewTrafficIncidentModalProps> = ({
  incidentId,
  onClose,
  open,
}) => {
  const { motorClubMembershipStore } = useCustomStore();
  const [details, setDetails] =
    React.useState<MotorClubTrafficIncidentDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !incidentId) {
      setDetails(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore
      .getTrafficIncidentDetails(incidentId)
      .then((responseOrErr) => {
        if (!isMounted) {
          return;
        }

        if (responseOrErr.isOk()) {
          setDetails(responseOrErr.value);
        }
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [incidentId, motorClubMembershipStore, open]);

  const formatDate = (value?: string) => {
    if (!value) {
      return '--';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
  };

  const getIncidentStatusColors = (status: string) => {
    const normalized = status?.toLowerCase?.() || '';
    if (
      normalized.includes('case fee paid') ||
      normalized.includes('case fee accepted')
    ) {
      return { bg: '#E7F5EC', color: '#2F8F4E' };
    }
    if (
      normalized.includes('case reported to law firm') ||
      normalized.includes('case representation cancelled')
    ) {
      return { bg: '#F1E9FB', color: '#6C3FB0' };
    }
    if (
      normalized.includes('under review') ||
      normalized.includes('incident reported')
    ) {
      return { bg: '#FBEED3', color: '#B9790A' };
    }
    if (normalized.includes('member declined coverage')) {
      return { bg: '#E7EEFB', color: '#2358A8' };
    }
    return { bg: '#EEEEF0', color: '#66666F' };
  };

  const statusColors = getIncidentStatusColors(details?.status || '');

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle
        sx={{ alignItems: 'center', display: 'flex', padding: '16px 22px' }}>
        <Typography
          sx={{
            color: '#2B2B2B',
            flex: 1,
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '16px',
          }}>
          Traffic Incident Details
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#66666F', padding: '4px' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: '20px 22px' }}>
        {isLoading ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '180px',
            }}>
            <CircularProgress size={26} sx={{ color: '#DEC330' }} />
          </Box>
        ) : details ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Box
              sx={{
                ...summaryCardSx,
                backgroundColor: statusColors.bg,
                borderColor: statusColors.color,
              }}>
              <Box>
                <Typography sx={labelSx}>Status</Typography>
                <Typography sx={{ ...valueSx, color: statusColors.color }}>
                  <Box component="span" sx={{ marginRight: '6px' }}>
                    {'●'}
                  </Box>
                  {details.status || 'Unknown'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={labelSx}>Incident Number</Typography>
                <Typography sx={valueSx}>{details.number || '--'}</Typography>
              </Box>
            </Box>
            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>Driver</Typography>
              <Typography sx={valueSx}>{details.driver || '--'}</Typography>
            </Box>
            <Box sx={twoColumnSx}>
              <Box sx={contentCardSx}>
                <Typography sx={labelSx}>Incident Date</Typography>
                <Typography sx={valueSx}>
                  {formatDate(details.dateIssued)}
                </Typography>
              </Box>
              <Box sx={contentCardSx}>
                <Typography sx={labelSx}>Date Submitted</Typography>
                <Typography sx={valueSx}>
                  {formatDate(details.dateSubmitted)}
                </Typography>
              </Box>
            </Box>
            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>State</Typography>
              <Typography sx={valueSx}>{details.state || '--'}</Typography>
            </Box>
            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>Driver Statement</Typography>
              <Typography sx={bodySx}>{details.statement || '--'}</Typography>
            </Box>
            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>Acknowledgements</Typography>
              <Typography sx={bodySx}>
                MVR: {details.mvrAcknowledged ? 'Yes' : 'No'}
              </Typography>
              <Typography sx={bodySx}>
                Attorney waiver: {details.attorneyAcknowledged ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography sx={bodySx}>
            Incident details could not be loaded.
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '14px 22px',
        }}>
        <Button onClick={onClose} sx={buttonSx} variant="contained">
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

const labelSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '11px',
  letterSpacing: '.4px',
  marginBottom: '4px',
  textTransform: 'uppercase',
};

const valueSx = {
  color: '#2B2B2B',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '14px',
};

const bodySx = {
  color: '#2B2B2B',
  fontFamily: 'FiraSans-Regular',
  fontSize: '13px',
  lineHeight: 1.5,
};

const contentCardSx = {
  border: '1px solid #E2E2E6',
  borderRadius: '10px',
  padding: '12px 14px',
};

const summaryCardSx = {
  ...contentCardSx,
  display: 'flex',
  justifyContent: 'space-between',
};

const twoColumnSx = {
  display: 'grid',
  gap: '12px',
  gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
};

const buttonSx = {
  '&:hover': { backgroundColor: '#B69300' },
  backgroundColor: '#C9A200',
  borderRadius: '8px',
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  textTransform: 'none',
};

export default ViewTrafficIncidentModal;
